/**
 * The backend sends timestamps in two formats:
 *   - ISO 8601:  "2024-06-01T14:05:30.123Z"
 *   - European:  "1.6.2024 14:5:30.123"  (D.M.YYYY H:m:s.SSS)
 *
 * `new Date()` only handles ISO reliably, so we parse the European
 * format manually when ISO parsing fails.
 */

const EUROPEAN_DATE_RE = /(\d+)\.(\d+)\.(\d+)\s+(\d+):(\d+):(\d+)(?:\.(\d+))?/;

/**
 * Parses a timestamp string (ISO or European format) into a Date.
 * Returns null if the string cannot be parsed.
 */
export function parseTimestamp(time: string): Date | null {
    const isoDate = new Date(time);
    if (!isNaN(isoDate.getTime())) return isoDate;

    const match = time.match(EUROPEAN_DATE_RE);
    if (!match) return null;

    const [, day, month, year, h, m, s, frac = '0'] = match;
    const ms = Number(frac.padEnd(3, '0').slice(0, 3));
    // NB: uses local timezone, matching the old moment.js behaviour.
    return new Date(+year, +month - 1, +day, +h, +m, +s, ms);
}

/**
 * Formats a timestamp string as "HH:mm:ss.SSS".
 * Returns the original string if it cannot be parsed.
 */
export function formatTimeHHmmssSSS(time: string): string {
    if (!time) return '';

    const date = parseTimestamp(time);
    if (!date) return time;

    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    const ms = String(date.getMilliseconds()).padStart(3, '0');
    return `${h}:${m}:${s}.${ms}`;
}

/**
 * Converts a timestamp string to ISO format for sorting.
 * Returns empty string if it cannot be parsed.
 */
export function toISOString(time: string): string {
    if (!time) return '';

    const date = parseTimestamp(time);
    if (!date) return '';

    return date.toISOString();
}
